import { useCallback } from 'react';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import {
  useCollectionsStore,
  setCollections,
  setSelectedRequest,
  setSelectedCollection,
  setSelectedFolder,
} from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { useAuthStore } from '../store/authStore';
import { setForkModalWorkspace, useUiModalStore } from '../store/uiModalStore';
import { isBackendWorkspace, generateLocalWorkspaceId, buildLocalManifestEntries } from '../utils/workspaceStorage';
import { collectionService } from '../services/collectionService';
import { workspaceService } from '../services/workspaceService';
import { saveWorkspaceSnapshot, loadSnapshotById } from '../services/workspaceStoreRouter';
import { saveLocalWorkspaceManifest, buildPublishLocalPayload } from '../services/localWorkspaceStore';
import { markLocalOnly } from '../services/localCollectionLink';
import { requireWorkspaceSlot } from '../plans/localWorkspaces';
import { requireApiRoom } from '../plans/localApiUsage';
import { countApiItems } from '../plans/apiCounts';

/** Fork a workspace into a new one. */
export function useWorkspaceFork({
  toastT,
  snapshotHydratedRef,
  setForkingWorkspaceId,
}) {
  /* ── Fork ──────────────────────────────────────────────────────────────── */
  const openForkWorkspaceModal = useCallback((workspace) => {
    if (workspace) setForkModalWorkspace(workspace);
  }, []);

  const deepCopyCollectionsForFork = (cols, workspaceId) =>
    (cols || []).map((c, ci) => {
      const colId = `${workspaceId}-col-${ci}`;
      return markLocalOnly({
        ...c,
        id: colId,
        cloud_collection_id: undefined,
        link_status: 'local_only',
        requests: (c.requests || []).map((r, ri) => ({
          ...r,
          id: `${workspaceId}-col-${ci}-req-${ri}`,
        })),
      });
    });

  const handleForkWorkspaceConfirm = useCallback(async (options) => {
    const workspace = useUiModalStore.getState().forkModalWorkspace;
    if (!workspace) return;
    setForkingWorkspaceId(workspace.id);
    try {
      const { destination, localTarget, existingLocalId, newLocalName, copyVariables } =
        options;
      // Plans v2: forking into a NEW local workspace counts.
      if (
        destination === 'local' &&
        (localTarget === 'new' || !existingLocalId) &&
        !requireWorkspaceSlot()
      ) {
        return;
      }

      const isAuthenticated = useAuthStore.getState().isAuthenticated;
      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
      const workspaces = useWorkspaceStore.getState().workspaces;
      const { collections } = useCollectionsStore.getState();
      const { globalVariables, workspaceVariables, environments, selectedEnvironment } =
        useVariablesStore.getState();

      if (destination === 'local') {
        let targetId = existingLocalId;
        let targetWs = workspaces.find((w) => String(w.id) === String(targetId));

        if (localTarget === 'new' || !targetId) {
          targetId = generateLocalWorkspaceId();
          targetWs = {
            id: targetId,
            local_uid: targetId,
            name: newLocalName || `${workspace.name} (Fork)`,
            type: 'personal',
            storage: 'local',
            source: 'personal',
            role: 'owner',
            settings: { fork_copy_variables: copyVariables },
            createdAt: Date.now(),
          };
          setWorkspaces((prev) => [...prev, targetWs]);
        }

        let sourceCols = collections;
        let sourceGlobals = globalVariables;
        let sourceWsVars = workspaceVariables;
        let sourceEnvs = environments;
        let sourceSelEnv = selectedEnvironment?.id || '';

        if (isBackendWorkspace(workspace, isAuthenticated)) {
          try {
            sourceCols = await collectionService.getCollections(workspace.id);
          } catch {
            sourceCols = [];
          }
        } else if (String(workspace.id) !== String(currentWorkspace?.id)) {
          const snap = await loadSnapshotById(workspaces, workspace.id);
          sourceCols = snap.collections || [];
          if (copyVariables) {
            sourceGlobals = snap.globalVariables || [];
            sourceWsVars = snap.workspaceVariables || [];
            sourceEnvs = snap.environments || [];
            sourceSelEnv = snap.selectedEnvironmentId || '';
          }
        } else if (copyVariables) {
          const snap = await loadSnapshotById(workspaces, workspace.id);
          sourceGlobals = snap.globalVariables?.length ? snap.globalVariables : globalVariables;
          sourceWsVars = snap.workspaceVariables?.length ? snap.workspaceVariables : workspaceVariables;
          sourceEnvs = snap.environments?.length ? snap.environments : environments;
          sourceSelEnv = snap.selectedEnvironmentId || selectedEnvironment?.id || '';
        }

        const forkedCollections = deepCopyCollectionsForFork(sourceCols, targetId);
        const existingSnap =
          localTarget === 'existing' && existingLocalId
            ? await loadSnapshotById(workspaces, targetId)
            : { collections: [], globalVariables: [], workspaceVariables: [], environments: [] };

        // Plans v2 card 3.3: forking INTO an existing local workspace adds its
        // collections and requests to that workspace (and, with variables,
        // replaces its environments). They have to fit, as an import does.
        // Counted across every own local workspace (the target is one of them).
        if (localTarget === 'existing' && existingLocalId) {
          const envsBefore = (existingSnap.environments || []).length;
          const envsAfter = copyVariables ? (sourceEnvs || []).length : envsBefore;
          const adding = { ...countApiItems(forkedCollections), environments: Math.max(0, envsAfter - envsBefore) };
          if (!(await requireApiRoom(adding, { target: { ...existingSnap, id: targetId } }))) return;
        }

        const targetWsObj = workspaces.find((w) => String(w.id) === String(targetId)) || targetWs;
        await saveWorkspaceSnapshot(targetWsObj, {
          collections: [...(existingSnap.collections || []), ...forkedCollections],
          globalVariables: copyVariables
            ? sourceGlobals
            : existingSnap.globalVariables || [],
          workspaceVariables: copyVariables
            ? sourceWsVars
            : existingSnap.workspaceVariables || [],
          environments: copyVariables ? sourceEnvs : existingSnap.environments || [],
          selectedEnvironmentId: copyVariables ? sourceSelEnv : existingSnap.selectedEnvironmentId || '',
        });
        await saveLocalWorkspaceManifest(
          buildLocalManifestEntries([...workspaces.filter((w) => String(w.id) !== String(targetId)), targetWs])
        );
        snapshotHydratedRef.current = null;
        setCurrentWorkspace(targetWs);
        setCollections(forkedCollections);
        snapshotHydratedRef.current = targetId;
        setForkModalWorkspace(null);
        toastT('toast.workspaceForkedLocal', 'success', { name: targetWs.name });
        return;
      }

      let result;
      if (!isBackendWorkspace(workspace, isAuthenticated)) {
        // Source is a local workspace — build payload from snapshot and publish to cloud.
        const snap = await loadSnapshotById(workspaces, workspace.id);
        const forkName = newLocalName || `${workspace.name} (Fork)`;
        const payload = buildPublishLocalPayload({ ...workspace, name: forkName }, snap);
        result = await workspaceService.publishLocal(payload);
      } else {
        result = await workspaceService.forkWorkspace(workspace.id);
      }

      let forked = result.workspace || result;

      setWorkspaces((prev) => [...(prev || []), forked]);
      setCurrentWorkspace(forked);
      if (result.collections) {
        setCollections(result.collections);
      } else if (forked.id) {
        const workspaceCollections = await collectionService.getCollections(forked.id);
        setCollections(workspaceCollections);
      }
      setSelectedRequest(null);
      setSelectedCollection(null);
      setSelectedFolder(null);
      setForkModalWorkspace(null);
      toastT('toast.workspaceForked', 'success', { name: forked.name });
    } catch (error) {
      console.error('Failed to fork workspace:', error);
      toastT('toast.forkWorkspaceFailed', 'error', { message: error.message });
    } finally {
      setForkingWorkspaceId(null);
    }
  }, [snapshotHydratedRef, toastT, setForkingWorkspaceId]);

  return { openForkWorkspaceModal, handleForkWorkspaceConfirm };
}
